import {
  ChangeDetectionStrategy,
  Component,
  OnChanges,
  OnInit,
  inject,
  input,
  output,
} from '@angular/core';
import { MatDatepicker } from '@angular/material/datepicker';
import { ActivatedRoute } from '@angular/router';
import { addMonths, format, parseISO } from 'date-fns';
import { shareReplay } from 'rxjs/operators';
import { QarepaProgressBarService } from '@qarepa/common';
import { YearMonthDatepickerForm } from './year-month-datepicker.form';

@Component({
  selector: 'qaroni-year-month-datepicker',
  templateUrl: './year-month-datepicker.component.html',
  styleUrls: ['./year-month-datepicker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class YearMonthDatepickerComponent
  extends YearMonthDatepickerForm
  implements OnInit, OnChanges
{
  readonly year = input<number>(0);
  readonly borderLess = input<boolean>(true);
  readonly monthName = input<string>('');
  readonly monthNumber = input<number>(0);
  readonly onlyCurrentYear = input<boolean>(false);
  readonly useQueryParam = input<boolean>(true);
  readonly date = output<string>();

  private route = inject(ActivatedRoute);
  private progressBar = inject(QarepaProgressBarService);

  public isLoading$ = this.progressBar.getProgressBar$().pipe(shareReplay(1));
  private queryParamMap$ = this.route.queryParamMap;

  private today = new Date();

  ngOnInit(): void {
    if (this.useQueryParam()) {
      this.subs.add(this.queryParamMap$.subscribe(this.getQueryParamMap));
    }
  }

  ngOnChanges(): void {
    if (!this.useQueryParam()) {
      const year = this.year() || this.today.getFullYear();
      const monthNumber = this.monthNumber() || this.today.getMonth() + 1;

      this.currentDate = new Date(year, monthNumber - 1);
      this.dateControl.setValue(format(this.currentDate, 'yyyy-MM'));
    }

    if (this.onlyCurrentYear()) {
      this.minDate = new Date(this.year(), 0, 1);
    }
  }

  get showDatepicker(): boolean {
    return this.monthName() && this.year() ? true : false;
  }

  get ngConditionalsClasses() {
    return {
      invisible: !(this.monthName() && this.year()),
      border: !this.borderLess(),
    };
  }

  public setMonthAndYear(
    normalizedMonthAndYear: Date,
    datepicker: MatDatepicker<Date>
  ) {
    this.dateControl.setValue(format(normalizedMonthAndYear, 'yyyy-MM'));
    this.date.emit(this.dateControl.value);
    datepicker.close();
  }

  public addMonth() {
    const date = addMonths(parseISO(this.dateControl.value), 1);
    this.dateControl.setValue(format(date, 'yyyy-MM'));
    this.date.emit(this.dateControl.value);
  }

  public subtractMonth() {
    const date = addMonths(parseISO(this.dateControl.value), -1);
    this.dateControl.setValue(format(date, 'yyyy-MM'));
    this.date.emit(this.dateControl.value);
  }
}
